import { FUNDING, JUDGEMENTS, P1_5_TEXT } from '../../i18n/el';

// Τα **ενεργά** φίλτρα, ως αφαιρέσιμα chips (Απόφαση D30).
//
// **Γιατί υπάρχει.** Με το Σετ Β η περιοχή των φίλτρων συμπτύσσεται, ώστε να μην
// καταλαμβάνει το μισό της οθόνης. Η σύμπτυξη όμως δημιουργεί ακριβώς την
// κατάσταση που ο κώδικας των ετικετών είχε ήδη ονομάσει: *φίλτρο που δεν
// φαίνεται είναι φίλτρο που ο χρήστης θα ξεχάσει, και θα διαβάσει το μικρό
// αποτέλεσμα ως έλλειψη δεδομένων* (ΜΛΑ-03). Ο κανόνας ίσχυε για μία στήλη
// chips ετικετών· η σύμπτυξη τον χρειάζεται για **δέκα** φίλτρα.
//
// Μετρητής στο κουμπί «Φίλτρα» δεν αρκεί, και ο λόγος είναι συγκεκριμένος: ο
// αριθμός λέει **πόσα** και όχι **ποια**, και το «(3)» δεν αφαιρείται με ένα
// πάτημα.
//
// **Ο κατάλογος των πεδίων είναι ΕΝΑΣ, και ελέγχεται.** Κάθε φίλτρο που κάνει
// αληθές το «υπάρχουν δομημένα φίλτρα;» οφείλει να έχει εγγραφή εδώ· αλλιώς
// υπάρχει φίλτρο που ανοίγει την περιοχή και **δεν εμφανίζεται πουθενά** όταν
// εκείνη κλείσει. Η αντιστοιχία των δύο φυλάσσεται από δοκιμή, γιατί είναι
// ακριβώς το είδος που αποκλίνει σιωπηλά με το επόμενο πεδίο.

const dateOf = (value) => new Date(value).toLocaleDateString('el-GR');

/**
 * Ένα πεδίο ανά γραμμή: πώς λέγεται, και πώς διαβάζεται η τιμή του.
 *
 * Οι **ετικέτες δεν είναι εδώ**: είναι πίνακας τιμών και όχι μία τιμή, οπότε
 * παράγουν ένα chip η καθεμία, με δικό της κουμπί αφαίρεσης.
 */
const FIELDS = [
    { name: 'agency', label: FUNDING.agency },
    { name: 'field', label: FUNDING.field },
    { name: 'deadlineBefore', label: FUNDING.deadlineBefore, format: dateOf },
    { name: 'minAmount', label: FUNDING.minAmount },
    { name: 'eligible', label: FUNDING.eligible },
    {
        name: 'relevance',
        label: FUNDING.relevance,
        format: (value) => JUDGEMENTS.relevance[value] ?? value
    },
    { name: 'createdBy', label: P1_5_TEXT.filterCreatedBy },
    { name: 'from', label: P1_5_TEXT.filterFrom, format: dateOf },
    { name: 'to', label: P1_5_TEXT.filterTo, format: dateOf }
];

/** Τα ονόματα των πεδίων, ώστε η δοκιμή να μη χρειάζεται να τα απαριθμήσει. */
export const ACTIVE_FILTER_FIELDS = Object.freeze(FIELDS.map((field) => field.name));

/**
 * Ένα chip με κουμπί αφαίρεσης.
 *
 * Ο στόχος αφαίρεσης είναι ολόκληρο το chip και όχι μόνο το «×»: ελάχιστο
 * μέγεθος στόχου, όπως ήδη ισχύει για τις ετικέτες.
 */
function FilterChip({ label, value, onRemove }) {
    return (
        <button
            type="button"
            onClick={onRemove}
            className="inline-flex items-center gap-1.5 rounded-full bg-accent px-3 py-1 text-sm text-accent-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
        >
            <span className="opacity-80">{`${label}:`}</span>
            <span className="font-medium">{value}</span>
            <i className="fa-solid fa-xmark" aria-hidden="true" />
            <span className="sr-only">{`${P1_5_TEXT.filterRemove} ${label}`}</span>
        </button>
    );
}

export default function ActiveFilters({ state, setFilter }) {
    const tags = state.tags ?? [];
    const active = FIELDS.filter((field) => Boolean(state[field.name]));

    if (tags.length === 0 && active.length === 0) return null;

    /**
     * Οι δύο χειριστές είναι **εργοστάσια ονομασμένων συναρτήσεων** και όχι
     * ενσωματωμένα βέλη μέσα στα χαρακτηριστικά: ο στατικός έλεγχος καλωδίωσης
     * διαβάζει τα χαρακτηριστικά ως κείμενο μέχρι το πρώτο `>`, και το βέλος
     * περιέχει ένα.
     */
    const removeField = (name) => function remove() {
        setFilter({ [name]: '' });
    };

    const removeTag = (tag) => function remove() {
        setFilter({ tags: tags.filter((other) => other !== tag) });
    };

    function clearAll() {
        setFilter(Object.fromEntries([
            ['tags', []],
            ...ACTIVE_FILTER_FIELDS.map((name) => [name, ''])
        ]));
    }

    return (
        <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-muted">{P1_5_TEXT.filterActive}</span>

            {tags.map((tag) => (
                <FilterChip
                    key={`tag:${tag}`}
                    label={P1_5_TEXT.filterTag}
                    value={tag}
                    onRemove={removeTag(tag)}
                />
            ))}

            {active.map((field) => (
                <FilterChip
                    key={field.name}
                    label={field.label}
                    value={field.format ? field.format(state[field.name]) : state[field.name]}
                    onRemove={removeField(field.name)}
                />
            ))}

            <button
                type="button"
                onClick={clearAll}
                className="text-sm text-muted underline underline-offset-4"
            >
                {P1_5_TEXT.filterClear}
            </button>
        </div>
    );
}
